import { ApiError } from "@/pages/auth/api";
import { API_BASE_URL } from "@/shared/config/apiBaseUrl";

export type ModelChoiceMove = {
  rank: number;
  uci: string;
  san: string;
  probability: number;
  logit: number | null;
};

export type ModelChoicePolicy = {
  label: "MODEL CHOICE";
  model_id: string;
  effective_prefix?: string;
  effective_elo?: number;
  temperature?: number;
  top_k: ModelChoiceMove[];
  legal_move_count: number;
};

export type ModelProbeResult = {
  model_id: string;
  position: {
    fen: string;
    moves: string[];
    side_to_move: string;
  };
  policy: ModelChoicePolicy;
};

export type ModelTaskCreated = {
  task_id: string;
  status: string;
  created_at: string;
};

export type ModelTaskStatus = {
  task_id: string;
  task_type: string;
  model_id: string;
  status: string;
  result: ModelProbeResult | null;
  error: {
    code: string;
    message: string;
    retryable: boolean;
  } | null;
};

export type CreateModelChoiceTaskPayload = {
  modelId: string;
  moves: string[];
  fen: string | null;
  topK: number;
  temperature: number;
  elo: number;
};

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail = `request failed with status ${response.status}`;
    try {
      const payload = (await response.json()) as { detail?: string };
      if (payload.detail) {
        detail = payload.detail;
      }
    } catch {
      // Keep fallback message when response body is not JSON.
    }
    throw new ApiError(detail, response.status);
  }

  return (await response.json()) as T;
}

export async function createModelChoiceTask(
  payload: CreateModelChoiceTaskPayload,
  signal?: AbortSignal,
): Promise<ModelTaskCreated> {
  const response = await fetch(`${API_BASE_URL}/api/model/probe-position`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    signal,
    body: JSON.stringify({
      model_id: payload.modelId,
      moves: payload.moves,
      fen: payload.fen,
      top_k: payload.topK,
      temperature: payload.temperature,
      prefix: "bullet",
      elo: payload.elo,
      include_policy: true,
      include_outcome: false,
    }),
  });

  return parseJson<ModelTaskCreated>(response);
}

export async function getModelTaskStatus(
  taskId: string,
  signal?: AbortSignal,
): Promise<ModelTaskStatus> {
  const response = await fetch(`${API_BASE_URL}/api/model/tasks/${taskId}`, {
    signal,
  });

  return parseJson<ModelTaskStatus>(response);
}

